import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  listPlansQuery,
  getPlanQuery,
  getPlanPricingQuery,
  createPlanMutation,
  updatePlanMutation,
  createPlanPricingMutation,
  updatePlanPricingMutation,
} from "../../Graphql/querries/planQuery";
import {
  ListPlansResponse,
  GetPlanResponse,
  GetPlanPricingResponse,
  CreatePlanResponse,
  UpdatePlanResponse,
  CreatePlanPricingResponse,
  UpdatePlanPricingResponse,
  ListPlansInput,
  GetPlanInput,
  GetPlanPricingInput,
  CreatePlanInput,
  UpdatePlanInput,
  CreatePlanPricingInput,
  UpdatePlanPricingInput,
} from "../../Graphql/types/plan";
import { useDispatch } from "react-redux";
import { setPlanList, setPlanDetail } from "../../store/slices/planSlice";

export const useGetPlanList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<ListPlansResponse, Error, ListPlansInput | void>({
    mutationKey: ["listPlans"],
    mutationFn: (input) =>
      fetchGraphQL(listPlansQuery, { input: input ?? {} }, setAlert),
    onSuccess: (response) => {
      dispatch(setPlanList(response.listPlans.planList ?? []));
    },
  });
};

export const useGetPlanDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<GetPlanResponse, Error, GetPlanInput>({
    mutationKey: ["getPlan"],
    mutationFn: (input) => fetchGraphQL(getPlanQuery, { input }, setAlert),
    onSuccess: (response) => {
      dispatch(setPlanDetail(response.getPlan.plan ?? null));
    },
  });
};

export const useGetPlanPricing = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<GetPlanPricingResponse, Error, GetPlanPricingInput>({
    mutationKey: ["getPlanPricing"],
    mutationFn: (input) =>
      fetchGraphQL(getPlanPricingQuery, { input }, setAlert),
  });
};

export const useCreatePlan = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<CreatePlanResponse, Error, CreatePlanInput>({
    mutationKey: ["createPlan"],
    mutationFn: (input) =>
      fetchGraphQL(createPlanMutation, { input }, setAlert),
  });
};

export const useUpdatePlan = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    UpdatePlanResponse,
    Error,
    { planId: string; input: UpdatePlanInput }
  >({
    mutationKey: ["updatePlan"],
    mutationFn: ({ planId, input }) =>
      fetchGraphQL(updatePlanMutation, { planId, input }, setAlert),
    onSuccess: (response) => {
      if (response.updatePlan.plan) {
        dispatch(setPlanDetail(response.updatePlan.plan));
      }
    },
  });
};

export const useCreatePlanPricing = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<CreatePlanPricingResponse, Error, CreatePlanPricingInput>({
    mutationKey: ["createPlanPricing"],
    mutationFn: (input) =>
      fetchGraphQL(createPlanPricingMutation, { input }, setAlert),
  });
};

export const useUpdatePlanPricing = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    UpdatePlanPricingResponse,
    Error,
    { planId: string; planPricingId: string; input: UpdatePlanPricingInput }
  >({
    mutationKey: ["updatePlanPricing"],
    mutationFn: ({ planId, planPricingId, input }) =>
      fetchGraphQL(
        updatePlanPricingMutation,
        { planId, planPricingId, input },
        setAlert,
      ),
  });
};
